/* eslint-disable quotes */
/**
 * 回傳 Meta Link 標籤
 *
 * @returns { canonical:, alternate, preconnect }
 */

import type { ResolvableLink } from '@unhead/vue';

export function useMetaLink() {
  const route = useRoute();
  const { $i18n } = useNuxtApp();

  const {
    blobDomain,
    blobDomainWithoutHttps,
    consoleDomainWithoutHttps,
    provideTargetUrl,
  } = useWebsite();

  const { isCN } = useLanguage();

  const { locale } = $i18n;

  // Canonical 標籤

  const canonical = computed((): ResolvableLink[] => {
    // Materials Page 不設定 canonical
    if ((route.name as string)?.includes('marketplace-materials-details')) {
      return [];
    }

    const contextLocale: EnumLocale = (() => {
      switch (locale.value) {
        case EnumLocale.EN_ASIA_PACIFIC:
        case EnumLocale.EN_AMERICAS:
        case EnumLocale.EN_EMEA:
          return EnumLocale.EN_INT;
        default:
          return locale.value as EnumLocale;
      }
    })();

    return [
      {
        rel: 'canonical',
        href: provideTargetUrl(contextLocale),
      },
    ];
  });

  // Alternate 標籤

  const alternate = computed((): ResolvableLink[] => {
    // 這些語系不進 alternate 叢集
    if (
      locale.value === EnumLocale.EN_ASIA_PACIFIC ||
      locale.value === EnumLocale.EN_AMERICAS ||
      locale.value === EnumLocale.EN_EMEA
    ) {
      return [];
    }

    const result = [];

    const locales = [
      { locale: EnumLocale.EN_INT, hreflang: 'en' },
      { locale: EnumLocale.EN_JP, hreflang: 'en-JP' },
      { locale: EnumLocale.EN_TW, hreflang: 'en-TW' },
      { locale: EnumLocale.EN_KR, hreflang: 'en-KR' },
      { locale: EnumLocale.ZH_TW, hreflang: 'zh-TW' },
      { locale: EnumLocale.JA_JP, hreflang: 'ja-JP' },
      { locale: EnumLocale.ZH_CN, hreflang: 'zh-CN' },
    ];

    locales.forEach(({ locale, hreflang }) => {
      result.push({
        rel: 'alternate',
        hreflang,
        href: provideTargetUrl(locale),
      });
    });

    result.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: provideTargetUrl(isCN.value ? EnumLocale.ZH_CN : EnumLocale.EN_INT),
    });

    return result;
  });

  // Preconnect 標籤

  const preconnect = computed((): ResolvableLink[] => {
    const result = [
      {
        rel: 'preconnect',
        crossorigin: 'anonymous',
        href: blobDomain.value,
      },
      {
        rel: 'dns-prefetch',
        href: blobDomainWithoutHttps.value,
      },
      {
        rel: 'dns-prefetch',
        href: consoleDomainWithoutHttps.value,
      },
      !isCN.value && {
        rel: 'dns-prefetch',
        crossorigin: 'anonymous',
        href: '//www.googletagmanager.com',
      },
    ];
    return result as ResolvableLink[];
  });

  // 用於 HTML lang Attribute 的 Locale Name

  const htmlLocaleName = computed((): string => {
    switch (locale.value) {
      case EnumLocale.EN_INT:
      case EnumLocale.EN_EMEA:
      case EnumLocale.EN_AMERICAS:
      case EnumLocale.EN_ASIA_PACIFIC:
        return 'en';
      case EnumLocale.JA_JP:
        return 'ja-JP';
      case EnumLocale.EN_JP:
        return 'en-JP';
      case EnumLocale.ZH_TW:
        return 'zh-Hant-TW';
      case EnumLocale.EN_TW:
        return 'en-TW';
      case EnumLocale.EN_KR:
        return 'en-KR';
      case EnumLocale.ZH_CN:
        return 'zh-Hans-CN';
      default:
        return 'en';
    }
  });

  return { canonical, alternate, preconnect, htmlLocaleName };
}
